<template>
	<section v-if="!selectedBranchLabelFresh" :aria-label="selectedHypermarketInstance?.name || undefined">
		<Transition name="fade" mode="out-in">
			<div v-if="selectedHypermarket" class="container">
				<h2
					v-if="selectedHypermarketInstance?.name"
					class="typo-h1 mb-6 md:mb-8"
					v-text="selectedHypermarketInstance.name"
				/>
				<nav class="overflow-x-auto mb-6 md:mb-8" :aria-label="`Záložky pro sekci ${selectedHypermarketInstance?.name}`">
					<HorizontalScroll role="tablist" class="px-4 md:px-0 flex gap-2" fade-edges>
						<li v-for="item in productTabs" :key="item.id" role="tab" class="inline-flex">
							<UiTabToggle
								:id="slugify(item.label, {lower: true})"
								:as="AbsoluteInternalLink"
								:to="{hash: '#' + slugify(item.label, {lower: true})}"
								:aria-controls="slugify(item.label, {lower: true})"
								:active="productActiveTab === item.id"
								@click.prevent="handleProductTabSelected(item.id)"
							>
								{{ item.label }}
							</UiTabToggle>
						</li>
					</HorizontalScroll>
				</nav>
				<div
					v-if="productActiveTab === 0"
					:id="`tabpanel-${slugify(getProductTabLabel(0), {lower: true})}`"
					role="tabpanel"
					:aria-labelledby="slugify(getProductTabLabel(0), {lower: true})"
				>
					<CarouselSkeleton
						v-if="data?.articles"
						:items="data.articles"
						class="mb-6 md:mb-12"
						list-class="inline-flex w-full min-w-max gap-4 lg:min-w-full lg:w-fit lg:grid lg:grid-cols-4"
						page-per-item
						snap-items
						center-item-snap
					>
						<template #item="{item: article, index}">
							<li :key="storyKey(article, index)" class="rem:w-[180px] xs:rem:w-[300px] lg:w-full">
								<ArticlePreviewTile
									:article="article"
									:link-label="article.content.component === 'PageTypeRecipesRecipeDetail' ? 'Zobrazit recept' : 'Zobrazit článek'"
									class="h-full"
								/>
							</li>
						</template>
					</CarouselSkeleton>
					<div class="w-full flex justify-center">
						<AbsoluteInternalLink
							class="btn btn-primary btn-lg max-sm:w-full max-sm:justify-center mx-auto md:mt-12"
							:to="{name: 'blog'}"
						>
							Načíst více článků
							<NuxtIcon
								name="arrow-right"
								class="text-2xl"
								filled
							/>
						</AbsoluteInternalLink>
					</div>
				</div>
				<div
					v-if="productActiveTab === 1"
					:id="`tabpanel-${slugify(getProductTabLabel(1), {lower: true})}`"
					role="tabpanel"
					:aria-labelledby="slugify(getProductTabLabel(1), {lower: true})"
				>
					<CarouselSkeleton
						v-if="data?.gastroProducts"
						:items="data.gastroProducts.result ?? []"
						class="mb-6 md:mb-12"
						list-class="inline-flex w-full min-w-max gap-6 xl:min-w-full xl:w-fit xl:grid xl:grid-cols-5"
						page-per-item
						snap-items
						center-item-snap
					>
						<template #firstItem>
							<li
								v-if="restaurantPromotionTileDesktop?.filename || restaurantPromotionTileMobile?.filename"
								class="relative"
							>
								<NuxtPicture
									v-if="restaurantPromotionTileDesktop?.filename"
									provider="storyblok"
									height="394"
									width="311"
									:modifiers="storyblokFocusModifiers(restaurantPromotionTileDesktop.focus)"
									:src="restaurantPromotionTileDesktop.filename"
									:alt="restaurantPromotionTileDesktop.alt || ''"
									sizes="311px"
									:img-attrs="{class:'rounded-2.5xl w-full'}"
									loading="lazy"
									class="hidden sm:block"
								/>
								<NuxtPicture
									v-if="restaurantPromotionTileMobile?.filename"
									provider="storyblok"
									height="280"
									width="228"
									:modifiers="storyblokFocusModifiers(restaurantPromotionTileMobile.focus)"
									:src="restaurantPromotionTileMobile.filename"
									:alt="restaurantPromotionTileMobile.alt || ''"
									sizes="228px"
									:img-attrs="{class:'rounded-2.5xl w-full'}"
									loading="lazy"
									class="sm:hidden"
								/>
							</li>
						</template>
						<template #item="{item: product}">
							<li :key="product.offerId" class="rem:w-[230px] xl:w-full">
								<GOOSProductTile :product="product" is-static class="h-full" />
							</li>
						</template>
					</CarouselSkeleton>
					<div class="w-full flex justify-center">
						<AbsoluteInternalLink
							class="btn btn-primary btn-lg max-sm:w-full max-sm:justify-center mx-auto md:mt-12"
							:to="branchSpecificPath({name: 'sluzby-slug', params: {slug: 'restaurace'}})"
						>
							Zobrazit více
							<NuxtIcon
								name="arrow-right"
								class="text-2xl"
								filled
							/>
						</AbsoluteInternalLink>
					</div>
				</div>
				<div
					v-if="productActiveTab === 2"
					:id="`tabpanel-${slugify(getProductTabLabel(2), {lower: true})}`"
					role="tabpanel"
					:aria-labelledby="slugify(getProductTabLabel(2), {lower: true})"
					class="rem:rounded-[20px] shadow-sm bg-background-secondary overflow-hidden lg:flex lg:bg-background-primary lg:p-5 lg:gap-10"
				>
					<template v-if="data?.gasStation?.content">
						<div class="relative overflow-hidden max-lg:aspect-[5/2] lg:w-1/4 rem:lg:min-w-[285px]">
							<NuxtPicture
								v-if="data?.gasStation?.content && data?.gasStation?.content?.image && data?.gasStation?.content?.image.filename"
								provider="storyblok"
								sizes="lg:800px inf:400px"
								:src="data?.gasStation?.content?.image.filename"
								:alt="data?.gasStation?.content?.image.alt || ''"
								:modifiers="storyblokFocusModifiers(data.gasStation.content?.image.focus)"
								class="absolute inset-0 size-full"
								:img-attrs="{class:'size-full object-cover rounded-[16px]'}"
								loading="lazy"
							/>
						</div>
						<div class="w-full p-4 sm:flex sm:flex-col lg:flex-row lg:flex-wrap lg:gap-y-10 lg:p-0">
							<div class="lg:w-3/4 lg:order-1">
								<h3 class="typo-h5 text-heading-secondary mb-2">
									Čerpací stanice vám nabízí:
								</h3>
								<RichText
									v-if="data?.gasStation?.content?.body?.[0].children?.[0].content?.[0].text"
									:data="data?.gasStation?.content?.body?.[0].children?.[0].content?.[0].text"
									class="typo-body-s [&_ul]:!mb-0 [&_ul_li:not(:last-child)]:!mb-1"
								/>
							</div>
							<hr class="my-4 border-border-secondary lg:hidden" />
							<div class="lg:order-3 lg:flex lg:rem:gap-x-[80px]">
								<div>
									<h3 class="typo-h5 text-heading-secondary mb-2">
										Ceny pohonných hmot
									</h3>
									<FuelPricelistTable
										v-if="isAnyFuelPumpOpen"
										:pricelist="firstBlok(data?.gasStation?.content?.fuelPricelist)"
										align-prices="left"
										class="w-full typo-body-s"
									/>
								</div>
								<hr class="my-4 border-border-secondary lg:hidden" />
								<div>
									<h3 class="typo-h5 text-heading-secondary mb-2">
										Provozní doba
									</h3>
									<OpeningHoursTable :entities="data!.openingHourEntities" class="grow typo-body-s" />
								</div>
							</div>
							<AbsoluteInternalLink
								:to="branchSpecificPath({name: 'sluzby-slug', params: {slug: data?.gasStation?.slug}})"
								class="btn btn-primary btn-lg w-full mt-8 justify-center sm:w-fit sm:mx-auto lg:order-2 lg:h-fit lg:mt-3 lg:mr-3"
							>
								Zjistit více
								<NuxtIcon
									name="arrow-right"
									filled
									class="text-2xl"
								/>
							</AbsoluteInternalLink>
						</div>
					</template>
				</div>
			</div>
			<RoundedPresentationalTilesSkeleton
				v-else
				title="Služby pro pohodlnější nákup"
				subtitle="V každém hypermarketu nabízíme služby, které usnadní váš nákup. Pro zobrazení konkrétních služeb nejdřív zvolte pobočku svého hypermarketu Globus."
				class="[--grid-cols-md:repeat(3,minmax(0,1fr))]"
				list-class="md:w-fit md:mx-auto"
			>
				<template #default>
					<RoundedPresentationalTileSkeleton variant="light">
						<template #image>
							<NuxtPicture
								src="/images/homepage/service-1.jpg"
								alt="Aktuality z prodejny"
								sizes="xs:175px sm:334px md:113px lg:187px xl:208px xxl:304px inf:304px"
								:img-attrs="{class:'w-full h-full object-cover d-block'}"
								loading="lazy"
							/>
						</template>
						<template #title>
							<AbsoluteInternalLink
								:to="{name: 'blog'}"
								class="before:cover-parent"
							>
								Aktuality z<br />
								prodejny
							</AbsoluteInternalLink>
						</template>
					</RoundedPresentationalTileSkeleton>
					<RoundedPresentationalTileSkeleton variant="light">
						<template #image>
							<NuxtPicture
								src="/images/homepage/service-2.jpg"
								alt="Restaurace"
								sizes="xs:175px sm:334px md:113px lg:187px xl:208px xxl:304px inf:304px"
								:img-attrs="{class:'w-full h-full object-cover d-block'}"
								loading="lazy"
							/>
						</template>
						<template #title>
							<AbsoluteInternalLink
								:to="branchSpecificPath({name: 'sluzby-slug', params: {branch: 'globus', slug: 'restaurace'}})"
								class="before:cover-parent"
							>
								Restaurace
							</AbsoluteInternalLink>
						</template>
					</RoundedPresentationalTileSkeleton>
					<RoundedPresentationalTileSkeleton variant="light">
						<template #image>
							<NuxtPicture
								src="/images/homepage/service-3.jpg"
								alt="Čerpací stanice a myčka"
								sizes="xs:175px sm:334px md:113px lg:187px xl:208px xxl:304px inf:304px"
								:img-attrs="{class:'w-full h-full object-cover d-block'}"
								loading="lazy"
							/>
						</template>
						<template #title>
							<AbsoluteInternalLink
								:to="branchSpecificPath({name: 'sluzby-slug', params: {branch: 'globus', slug: 'cerpaci-stanice-a-myci-linka'}})"
								class="before:cover-parent"
							>
								Čerpací stanice<br />
								a myčka
							</AbsoluteInternalLink>
						</template>
					</RoundedPresentationalTileSkeleton>
				</template>
			</RoundedPresentationalTilesSkeleton>
		</Transition>
	</section>
</template>

<script setup lang="ts">
	import slugify from 'slugify';
	import type {ISbStoryData} from 'storyblok-js-client';
	import {storeToRefs} from 'pinia';
	import {getCommonStoryParams} from '~/storyblok/api/options';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useStoryblokGasStation} from '~/stores/storyblokGasStation';
	import {unref} from '#imports';
	import type {OfferResultCollectionResult} from '~/openapi';
	import type {OpeningHoursEntity} from '~/utils/openingHours';
	import type {
		AutomaticCarWashStoryblok,
		PageTypeBlogArticleDetailStoryblok,
		PageTypeGasStationStoryblok,
		PageTypePressReleaseDetailStoryblok,
		PageTypeRecipesRecipeDetailStoryblok,
		PageTypeRestaurantStoryblok,
		AssetStoryblok,
	} from '~/storyblok/types';
	import {ProductionTypeIdEnum} from '~/stores/shoppingCart';
	import {useGoosGastroStore} from '~/stores/goos/gastro';
	import {RESTAURANT_MEAL_PRESENTATION_FRONTEND_ID} from '#shared/constants';
	import useBranchPageAvailability from '~/composables/useBranchPageAvailability';
	import {productIsPreferredMenu} from '~/utils/productIsPreferredMenu';

	type Props = {
		restaurantPromotionTileDesktop?: AssetStoryblok;
		restaurantPromotionTileMobile?: AssetStoryblok;
	};

	defineProps<Props>();

	const route = useRoute();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {$storyblokApi} = useNuxtApp();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const selectedBranchLabelFresh = computed(() => selectedHypermarketInstance.value?.branchType === 'fresh');
	const gasStationStore = useStoryblokGasStation();
	const goosGastroStore = useGoosGastroStore();
	const branchSpecificPath = useBranchSpecificPath();
	const {data: branchPageAvailability, loading: branchPageAvailabilityLoading} = useBranchPageAvailability(selectedHypermarket);

	const AbsoluteInternalLink = resolveComponent('AbsoluteInternalLink');

	const {data} = await useAsyncData(
		'hypermarketServiceDetails',
		async (): Promise<{
			openingHourEntities: OpeningHoursEntity[],
			restaurant?: PageTypeRestaurantStoryblok,
			gasStation?: ISbStoryData<PageTypeGasStationStoryblok>,
			carWash?: AutomaticCarWashStoryblok,
			gastroProducts?: OfferResultCollectionResult | null,
			articles?: ISbStoryData<PageTypeBlogArticleDetailStoryblok | PageTypePressReleaseDetailStoryblok | PageTypeRecipesRecipeDetailStoryblok>[]
		} | null> => {
			if (!selectedHypermarketInstance.value) {
				return null;
			}

			try {
				const shouldFetchRestaurantProducts
					= !branchPageAvailabilityLoading.value
						&& branchPageAvailability.value.hasRestaurant;

				const [
					gasStationResponse,
					articlesResponse,
					gastroProducts,
				] = await Promise.all([
					gasStationStore.fetch(String(selectedHypermarket.value)),
					$storyblokApi.get('stories', {
						...getCommonStoryParams(),
						starts_with: '',
						is_startpage: false,
						per_page: 3,
						filter_query: {
							...selectedHypermarketInstance.value
								? {hypermarket: {any_in_array: selectedHypermarketInstance.value.uuid}}
								: undefined,
							component: {like: 'PageType*Detail'},
						},
					}),
					shouldFetchRestaurantProducts
						? goosGastroStore.fetchOffers({
							localityId: Number(selectedHypermarketInstance.value.content.gsoaId),
							productTypeId: ProductionTypeIdEnum.RESTAURANT,
							offset: 0,
							limit: 5,
							frontendId: RESTAURANT_MEAL_PRESENTATION_FRONTEND_ID,
							onlyOrderableNow: false,
						}).catch(() => null)
						: Promise.resolve(null),
				]);

				const gasStation = unref(gasStationResponse);

				return {
					openingHourEntities: [
						...gasStation ? extractGasStationOpeningHours(gasStation) : [],
					],
					gasStation,
					gastroProducts: gastroProducts?.result
						? {
							...gastroProducts,
							result: [...gastroProducts.result].sort((a, b) => Number(productIsPreferredMenu(b)) - Number(productIsPreferredMenu(a))),
						}
						: gastroProducts,
					articles: articlesResponse.data.stories as ISbStoryData<PageTypeBlogArticleDetailStoryblok | PageTypePressReleaseDetailStoryblok | PageTypeRecipesRecipeDetailStoryblok>[],
				};
			} catch (e: any) {
				console.error(e);
				return null;
			}
		},
		{watch: [selectedHypermarket, branchPageAvailability, branchPageAvailabilityLoading]},
	);

	const isAnyFuelPumpOpen = computed(() => (
		!!data.value?.gasStation?.content?.fuelPump?.[0]?.open
		|| !!data.value?.gasStation?.content?.selfServiceFuelPump?.[0]?.open
	));

	const productActiveTab = ref<number>(0);

	const productTabs = computed<any[]>(() => [
		{
			id: 0,
			label: 'Aktuality z prodejny',
		},
		data.value?.gastroProducts?.result?.length
			? {
				id: 1,
				label: 'Restaurace',
			}
			: null,
		data.value?.gasStation?.content
			? {
				id: 2,
				label: 'Čerpací stanice a myčka',
			}
			: null,
	].filter(Boolean));

	function handleProductTabSelected(id: number) {
		productActiveTab.value = id;
	}

	function getProductTabLabel(id: number) {
		return productTabs.value.find((item) => item.id === id)?.label || '';
	}

	onMounted(() => {
		if (route.hash) {
			const tabId = productTabs.value.findIndex((item) => slugify(item.label, {lower: true}) === route.hash.slice(1));
			if (tabId > 0) {
				productActiveTab.value = tabId;
			}
		}
	});
</script>
